import React from 'react';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { BRAND } from '../../theme/brandTokens';

const ringPulse = keyframes`
  0% {
    transform: scale(0.85);
    opacity: 0.35;
  }
  50% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(0.85);
    opacity: 0.35;
  }
`;

const ringRotateClockwise = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const ringRotateCounterClockwise = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(-360deg);
  }
`;

const SIZE_MAP = {
  small: { outer: 28, ring: 2 },
  medium: { outer: 44, ring: 3 },
  large: { outer: 56, ring: 3.5 },
};

const SalovareLoader = ({
  size = 'medium',
  message,
  showMessage = false,
  center = false,
  messageSx,
  sx,
}) => {
  const { t } = useTranslation();
  const dimensions = SIZE_MAP[size] || SIZE_MAP.medium;
  const displayMessage = message ?? (showMessage ? t('common.loading') : null);

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-busy="true"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: center ? 'center' : 'flex-start',
        gap: 1.5,
        py: center ? 0 : 2,
        ...sx,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: dimensions.outer,
          height: dimensions.outer,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            animation: `${ringRotateClockwise} 1.4s linear infinite`,
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: `${dimensions.ring}px solid transparent`,
              borderTopColor: BRAND.accent,
              animation: `${ringPulse} 1.1s ease-in-out infinite`,
            }}
          />
        </Box>
        <Box
          sx={{
            position: 'absolute',
            inset: dimensions.ring * 2,
            animation: `${ringRotateCounterClockwise} 1.4s linear infinite`,
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: `${Math.max(dimensions.ring - 0.5, 1.5)}px solid transparent`,
              borderBottomColor: BRAND.accentLight,
              animation: `${ringPulse} 1.1s ease-in-out infinite`,
              animationDelay: '0.18s',
            }}
          />
        </Box>
      </Box>

      {displayMessage ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontWeight: 500, letterSpacing: 0.2, ...messageSx }}
        >
          {displayMessage}
        </Typography>
      ) : null}
    </Box>
  );
};

export default SalovareLoader;
